import { graphql, navigate, useStaticQuery } from 'gatsby'
import React from 'react'
import { useLang } from '../../hooks/useLang'
import { useRandomArticle } from '../../hooks/useRandomArticle'
import { useSiteId } from '../../hooks/useSiteId'
import DynamicLink from '../atoms/DynamicLink'
import SocialLink from '../atoms/SocialLink'
import BoswIcon from '../icons/BoswIcon'
import FacebookIcon from '../icons/FacebookIcon'
import InstagramIcon from '../icons/InstagramIcon'
import KununuIcon from '../icons/KununuIcon'
import LinkedInIcon from '../icons/LinkedInIcon'
import YouTubeIcon from '../icons/YouTubeIcon'
import FooterContactForm from '../molecules/FooterContactForm'
import FooterLiftStorys from '../molecules/FooterLiftStorys'
import FooterNewsletterAnmelden from '../molecules/FooterNewsletterAnmelden'
import FooterPlanungstools from '../molecules/FooterPlanungstools'
import FooterUberUns from '../molecules/FooterUberUns'
import FooterUnsereStandorte from '../molecules/FooterUnsereStandorte'
import StorungMelden from '../molecules/StorungMelden'

const Footer = ({ alternates }) => {
  const randomArticle = useRandomArticle()
  const siteId = useSiteId()
  const locale = useLang()

  const {
    craftContent: { entries },
  } = useStaticQuery(graphql`
    {
      craftContent {
        entries(type: "footer", section: "footer", siteId: [1, 2, 3]) {
          id
          siteId
          ... on CraftContent_footer_footer_Entry {
            footerSettings {
              __typename
              ... on CraftContent_footerSettings_linksColumn_BlockType {
                id
                heading {
                  ...linkFragment
                }
                links {
                  ... on CraftContent_links_BlockType {
                    linkItem {
                      ...linkFragment
                    }
                  }
                }
              }
              ... on CraftContent_footerSettings_aboutUs_BlockType {
                id
                aboutUsHeading: heading {
                  ...linkFragment
                }
                aboutUsBody: body
              }
              ... on CraftContent_footerSettings_planningTool_BlockType {
                id
                planningToolHeading: heading
                planningToolBody: body
              }
              ... on CraftContent_footerSettings_reportFault_BlockType {
                id
                reportFaultHeading: heading {
                  ...linkFragment
                }
                reportFaultBody: body
                phoneNumber
              }
              ... on CraftContent_footerSettings_newsletter_BlockType {
                id
                newsletterHeading: heading
                newsletterBody: body
              }
              ... on CraftContent_footerSettings_legalsLinks_BlockType {
                id
                legalsLinks: links {
                  ... on CraftContent_links_BlockType {
                    linkItem {
                      ...linkFragment
                    }
                  }
                }
              }
              ... on CraftContent_footerSettings_contactTool_BlockType {
                id
                pageLink {
                  ...linkFragment
                }
              }
            }
          }
        }
      }
    }
  `)
  const entry = entries.find(e => e.siteId === siteId)
  const linksColumnSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_linksColumn_BlockType',
  )
  const aboutUsSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_aboutUs_BlockType',
  )
  const planningToolSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_planningTool_BlockType',
  )
  const reportFaultSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_reportFault_BlockType',
  )
  const newsletterSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_newsletter_BlockType',
  )
  const legalsLinksSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_legalsLinks_BlockType',
  )
  const contactPageSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_contactTool_BlockType',
  )

  return (
    <footer className="mt-24 bg-white md:mt-32 xl:mt-36 pt-11 xl:pt-[74px] pb-8">
      <div className="container">
        <div className="pl-2 md:hidden">
          <StorungMelden
            isMobile
            heading={reportFaultSettings.reportFaultHeading}
            body={reportFaultSettings.reportFaultBody}
            phoneNumber={reportFaultSettings.phoneNumber}
          />
        </div>

        <div className="justify-between hidden space-x-24 md:flex xl:hidden">
          <div className="flex-1">
            <FooterUnsereStandorte {...linksColumnSettings} />
          </div>
          <div className="flex-1">
            <StorungMelden
              isMobile
              heading={reportFaultSettings.reportFaultHeading}
              body={reportFaultSettings.reportFaultBody}
              phoneNumber={reportFaultSettings.phoneNumber}
            />
          </div>
        </div>

        <div className="justify-between hidden space-x-24 md:flex xl:hidden">
          <div className="flex-1">
            <FooterUberUns
              heading={aboutUsSettings.aboutUsHeading}
              body={aboutUsSettings.aboutUsBody}
            />
          </div>
          <div className="flex-1">
            <FooterLiftStorys
              title={randomArticle.title}
              slug={randomArticle.slug}
              featureImage={randomArticle.featureImage[0]}
              isMobile
            />
          </div>
        </div>

        <div className="justify-between hidden pt-16 space-x-24 md:flex xl:hidden">
          <div className="flex-1">
            <FooterPlanungstools
              heading={planningToolSettings.planningToolHeading}
              body={planningToolSettings.planningToolBody}
            />
          </div>
          <div className="flex-1">
            {newsletterSettings && (
              <FooterNewsletterAnmelden
                heading={newsletterSettings.newsletterHeading}
                body={newsletterSettings.newsletterBody}
              />
            )}
            <FooterContactForm link={contactPageSettings.pageLink} />
          </div>
        </div>

        <div className="flex flex-col justify-between md:hidden xl:flex xl:flex-row">
          <FooterUnsereStandorte {...linksColumnSettings} />
          <FooterLiftStorys
            title={randomArticle.title}
            slug={randomArticle.slug}
            featureImage={randomArticle.featureImage[0]}
          />
          <div className="flex-[2] lg:pr-28 lg:mt-5.5">
            <FooterUberUns
              heading={aboutUsSettings.aboutUsHeading}
              body={aboutUsSettings.aboutUsBody}
            />
            <div className="pl-2 lg:hidden pt-5.5">
              <FooterLiftStorys
                isMobile
                title={randomArticle.title}
                slug={randomArticle.slug}
                featureImage={randomArticle.featureImage[0]}
              />
            </div>
            <FooterPlanungstools
              heading={planningToolSettings.planningToolHeading}
              body={planningToolSettings.planningToolBody}
            />
            <div className="pl-2 lg:hidden pt-5.5">
              <FooterContactForm link={contactPageSettings.pageLink} isMobile />
            </div>
          </div>
          <div className="pl-2 lg:w-64 md:pl-0 ">
            <StorungMelden
              heading={reportFaultSettings.reportFaultHeading}
              body={reportFaultSettings.reportFaultBody}
              phoneNumber={reportFaultSettings.phoneNumber}
            />
            {newsletterSettings && (
              <FooterNewsletterAnmelden
                heading={newsletterSettings.newsletterHeading}
                body={newsletterSettings.newsletterBody}
              />
            )}
            <FooterContactForm link={contactPageSettings.pageLink} />
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-start md:justify-between md:mt-14">
          <div className="pb-11 md:pb-0">
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-all hover:shadow-tpmp"
              href="https://www.linkedin.com/posts/as_aufzuge_ag_asaufzugege-lift-aufzug-activity-7062337764557508608-SyBE?utm_source=share&utm_medium=member_desktop"
            >
              <BoswIcon />
            </a>
          </div>
          <div className="flex items-center space-x-4 xl:w-64">
            <SocialLink href="https://www.facebook.com/as.aufzuege.ag" Icon={FacebookIcon} />
            <SocialLink href="https://www.instagram.com/as.aufzuege/" Icon={InstagramIcon} />
            <SocialLink href="https://www.youtube.com/c/LiftCh" Icon={YouTubeIcon} />
            <SocialLink href="https://www.linkedin.com/company/as_aufzuge_ag/" Icon={LinkedInIcon} />
            <SocialLink href="https://www.kununu.com/ch/as-aufzuege" Icon={KununuIcon} />
          </div>
        </div>

        <div className="md:flex items-start xl:items-center justify-between pt-8 mt-11 lg:mt-16 text-[13px] text-black border-t border-gray-4">
          <div>
            <ul className="flex flex-wrap items-center xl:space-x-4">
              {legalsLinksSettings.legalsLinks.map((legalLink, index) => (
                <li
                  key={`${legalLink.text}${index}`}
                  className="mb-4 mr-4 xl:mr-0 xl:mb-0"
                >
                  <DynamicLink
                    link={legalLink.linkItem}
                    className="underline transition-colors hover:text-red-as"
                  />
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center justify-between flex-1 flex-shrink-0 mt-4 mr-10 space-x-4 whitespace-pre md:mr-0 md:justify-end md:mt-0 md:pl-16 xl:pl-0 md:text-right">
            <div>© AS Aufzüge 2022</div>
            <select
              className="z-40 text-xs text-white bg-black border-0 u-lang-select"
              value={
                alternates && locale in alternates
                  ? alternates[locale]
                  : `/${locale}`
              }
              onChange={e => {
                window.localStorage.setItem('langOverride', '1')
                navigate(e.target.value)
              }}
            >
              <option
                value={alternates && 'de' in alternates ? alternates.de : '/de'}
              >
                Deutsch
              </option>
              <option
                value={alternates && 'fr' in alternates ? alternates.fr : '/fr'}
              >
                Français
              </option>
              <option
                value={alternates && 'it' in alternates ? alternates.it : '/it'}
              >
                Italiano
              </option>
            </select>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
